import React from 'react'

import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import { useCustomerRevealApiKeyCreate } from 'features/customers/hooks/useCustomerRevealApiKeyCreate'
import { REVEAL_API_LIMIT_LABELS } from 'features/customers/utils/features'

import { FormTextField } from 'components/input'
import { DescriptionListTable } from 'components/table'
import { withForm } from 'decorators/withForm'
import { parseDomainAllowlistInput } from 'validators/domain-allowlist'
import { generateRevealApiKeyValidator } from 'validators/reveal-api'

import RevealApiLimitField from './RevealApiLimitField'

const config = [
  {
    title: REVEAL_API_LIMIT_LABELS.revealApiRequestsLimit,
    render: () => <RevealApiLimitField name="revealApiRequestsLimit" />,
  },
  {
    title: REVEAL_API_LIMIT_LABELS.revealApiIdentifiedLimit,
    render: () => <RevealApiLimitField name="revealApiIdentifiedLimit" />,
  },
  {
    title: REVEAL_API_LIMIT_LABELS.revealApiUniqueIdentifiedIpsLimit,
    render: () => <RevealApiLimitField name="revealApiUniqueIdentifiedIpsLimit" />,
  },
  {
    title: 'Domain Allowlist',
    render: () => (
      <FormTextField
        helperText="Comma-separated list of allowed domains, e.g. example.com, app.example.com"
        name="publicApiKeyDomainAllowlist"
        placeholder="example.com, app.example.com"
      />
    ),
  },
]

type GenerateApiKeyModalProps = {
  handleSubmit: (...args: any[]) => any
  initialValues: Record<string, any>
  onClose: (...args: any[]) => any
}

const GenerateApiKeyModal = ({ handleSubmit, onClose, initialValues }: GenerateApiKeyModalProps) => {
  const { createRevealApiKey, isPending: isLoading } = useCustomerRevealApiKeyCreate()

  const onSubmit = async values => {
    await createRevealApiKey({
      requestQuota: values.revealApiRequestsLimit,
      identifiedQuota: values.revealApiIdentifiedLimit,
      uniqueIdentifiedIpsQuota: values.revealApiUniqueIdentifiedIpsLimit,
      publicApiKeyDomainAllowlist: parseDomainAllowlistInput(values.publicApiKeyDomainAllowlist),
    })
    onClose()
  }

  return (
    <Dialog onClose={onClose} open>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Generate new key</DialogTitle>
        <DialogContent style={{ width: 500 }}>
          {initialValues.apiKey && (
            <DialogContentText>
              Are you sure? This will require the customer to update their configuration to use the new key instead of
              the old one.
            </DialogContentText>
          )}
          <DescriptionListTable config={config} titleCellStyle={{ width: 200 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button color="primary" loading={isLoading} type="submit">
            Generate
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default withForm({ form: 'revealApiCreate', validate: generateRevealApiKeyValidator })(GenerateApiKeyModal)
